/**
 * Performance Table Component for Supplier Performance
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Pagination } from '../../components/ui/pagination';
import { Loader2 } from 'lucide-react';
import { SupplierProfile, SupplierRatingThreshold } from '../../lib/api';
import { getGradeInfo, getRatingStyle, getOTDColor, formatCurrency, isNARating } from './utils';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';

interface PerformanceTableProps {
  suppliers: SupplierProfile[];
  ratingThresholds: SupplierRatingThreshold[];
  currency: string;
  loading?: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
    hasMultiplePages: boolean;
    goToPage: (page: number) => void;
  };
  onOpenGRNModal: (supplierId: number, supplierName: string, filterType?: string, value?: number) => void;
  onOpenNCRModal: (supplierId: number, supplierName: string, filterType?: string, value?: number) => void;
  onOpenPOLineModal: (
    supplierId: number,
    supplierName: string,
    filterType?: string,
    value?: number,
    additionalFilters?: { status?: string; on_hold?: boolean }
  ) => void;
}

export function PerformanceTable({
  suppliers,
  ratingThresholds,
  currency,
  loading,
  pagination,
  onOpenGRNModal,
  onOpenNCRModal,
  onOpenPOLineModal,
}: PerformanceTableProps) {
  const qualityLabel = useQualityMeasureLabel();
  const getLetterGrade = (score: number | undefined) =>
    getGradeInfo(score, ratingThresholds).label;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Supplier Performance Overview ({pagination.totalCount})</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : suppliers.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No suppliers found</p>
        ) : (
          <>
            {/* Top Pagination */}
            {pagination.hasMultiplePages && (
              <div className="px-6 pt-4">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={pagination.goToPage}
                  itemLabel="suppliers"
                />
              </div>
            )}

            <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
              <table className="w-full min-w-[2400px]">
                {/* Multi-level Header */}
                {/* CR-4313: Sticky headers so column labels stay visible when scrolling */}
                <thead className="sticky top-0 z-20">
                  {/* Top-level header groups */}
                  <tr className="border-b bg-muted/50">
                    <th
                      className="p-3 text-left font-semibold sticky left-0 bg-muted/50 z-10"
                      style={{ minWidth: '200px' }}
                      rowSpan={2}
                    >
                      Supplier
                    </th>
                    <th
                      className="p-3 text-center font-semibold sticky bg-muted/50 z-10"
                      style={{ minWidth: '120px', left: '200px' }}
                      rowSpan={2}
                    >
                      Supplier Code
                    </th>
                    <th
                      className="p-3 text-center font-semibold sticky bg-muted/50 z-10"
                      style={{ minWidth: '200px', left: '320px' }}
                      rowSpan={2}
                    >
                      Customer
                    </th>
                    <th
                      className="p-3 text-center font-semibold sticky bg-muted/50 z-10 border-r"
                      style={{ minWidth: '100px', left: '520px' }}
                      rowSpan={2}
                    >
                      Rating
                    </th>
                    <th className="p-3 text-center font-semibold" style={{ minWidth: '120px' }} rowSpan={2}>
                      Warehouse
                    </th>
                    <th
                      className="p-3 text-center font-semibold border-l"
                      style={{ minWidth: '700px' }}
                      colSpan={7}
                    >
                      Quality
                    </th>
                    <th
                      className="p-3 text-center font-semibold border-l"
                      style={{ minWidth: '500px' }}
                      colSpan={5}
                    >
                      Cost
                    </th>
                    <th
                      className="p-3 text-center font-semibold border-l"
                      style={{ minWidth: '500px' }}
                      colSpan={5}
                    >
                      Delivery (PO Line)
                    </th>
                    <th
                      className="p-3 text-center font-semibold border-l"
                      style={{ minWidth: '400px' }}
                      colSpan={4}
                    >
                      Delivery (GRN)
                    </th>
                    <th
                      className="p-3 text-center font-semibold border-l"
                      style={{ minWidth: '150px' }}
                      colSpan={1}
                    >
                      CO2 Emissions
                    </th>
                  </tr>
                  {/* Second-level headers */}
                  <tr className="border-b bg-muted/30">
                    <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '100px' }}>
                      Qty Received
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
                      Qty Rejected
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '120px' }}>
                      Customer Liability {qualityLabel}
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '120px' }}>
                      Supplier Liability {qualityLabel}
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
                      Total {qualityLabel}
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      No. of NCRs
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Response Score
                    </th>
                    <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '120px' }}>
                      Total Value
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
                      Reject Value
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
                      Customer Cost of Quality
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
                      Supplier Cost of Quality
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Total Cost of Quality
                    </th>
                    <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '80px' }}>
                      PO Lines
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      OTIF
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Arrears
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
                      OTIF Score
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Qty Diff
                    </th>
                    <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '80px' }}>
                      No. of GRNs
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Ontime
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Late
                    </th>
                    <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
                      Early
                    </th>
                    <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '150px' }}>
                      Total CO2 Emissions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {suppliers.map((supplier) => (
                    <tr key={supplier.id} className="border-b hover:bg-muted/30 transition-colors">
                      {/* Supplier Name - Frozen */}
                      <td className="p-3 sticky left-0 bg-background z-10">
                        <div className="flex items-center gap-2">
                          {supplier.is_preferred && (
                            <span className="text-yellow-500" title="Strategic Supplier">
                              ★
                            </span>
                          )}
                          <div>
                            <div className="font-medium text-sm">{supplier.company_name}</div>
                          </div>
                        </div>
                      </td>

                      {/* Supplier Code - Frozen */}
                      <td className="p-3 text-center sticky bg-background z-10" style={{ left: '200px' }}>
                        <span className="text-xs">{supplier.supplier_code || '-'}</span>
                      </td>

                      {/* Customer Sites - Frozen */}
                      <td className="p-3 sticky bg-background z-10" style={{ left: '320px' }}>
                        <span className="text-xs">{supplier.customers || '-'}</span>
                      </td>

                      {/* Rating - Frozen */}
                      <td className="p-3 text-center sticky bg-background z-10 border-r" style={{ left: '520px' }}>
                        {isNARating(supplier) ? (
                          <Badge variant="secondary" className="font-bold">
                            N/A
                          </Badge>
                        ) : (
                          <Badge
                            className="font-bold"
                            style={getRatingStyle(supplier.combined_score ?? undefined, ratingThresholds)}
                          >
                            {getLetterGrade(supplier.combined_score ?? undefined)}
                          </Badge>
                        )}
                      </td>

                      {/* Warehouse */}
                      <td className="p-3 text-center">
                        <span className="text-xs">{supplier.warehouse_name || '-'}</span>
                      </td>

                      {/* Quality Metrics */}
                      <td className="p-3 text-center border-l">
                        <button
                          onClick={() =>
                            onOpenGRNModal(
                              supplier.company,
                              supplier.company_name,
                              'Qty Received',
                              supplier.qty_received
                            )
                          }
                          className="text-sm text-primary hover:underline cursor-pointer"
                          title="View GRN details"
                        >
                          {supplier.qty_received || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenNCRModal(
                              supplier.company,
                              supplier.company_name,
                              'Qty Rejected',
                              supplier.reject_qty
                            )
                          }
                          className="text-sm text-red-600 hover:underline cursor-pointer"
                          title="View NCR details"
                        >
                          {supplier.reject_qty || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{supplier.customer_liability_ppm?.toFixed(2) || 0}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{supplier.supplier_liability_ppm?.toFixed(2) || 0}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm font-medium">{supplier.total_ppm?.toFixed(2) || 0}</span>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenNCRModal(supplier.company, supplier.company_name, 'NCRs', supplier.ncr_count)
                          }
                          className="text-sm text-primary hover:underline cursor-pointer"
                          title="View NCR details"
                        >
                          {supplier.ncr_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{supplier.response_score || 0}</span>
                      </td>

                      {/* Cost Metrics */}
                      <td className="p-3 text-center border-l">
                        <button
                          onClick={() =>
                            onOpenGRNModal(
                              supplier.company,
                              supplier.company_name,
                              'Total Value',
                              parseFloat(supplier.total_value || '0')
                            )
                          }
                          className="text-sm font-medium text-primary hover:underline cursor-pointer"
                          title="View GRN details"
                        >
                          {formatCurrency(supplier.total_value || null, supplier.currency || currency)}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenNCRModal(
                              supplier.company,
                              supplier.company_name,
                              'Reject Value',
                              parseFloat(supplier.reject_value || '0')
                            )
                          }
                          className="text-sm text-red-600 hover:underline cursor-pointer"
                          title="View NCR details"
                        >
                          {formatCurrency(supplier.reject_value || null, supplier.currency || currency)}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{supplier.customer_cost_of_quality || 0}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{supplier.supplier_cost_of_quality || 0}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{supplier.total_cost_of_quality || 0}</span>
                      </td>

                      {/* Delivery Metrics (PO Line) */}
                      <td className="p-3 text-center border-l">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              supplier.company,
                              supplier.company_name,
                              'PO Lines',
                              supplier.po_line_count
                            )
                          }
                          className="text-sm text-primary hover:underline cursor-pointer"
                          title="View PO Line details"
                        >
                          {supplier.po_line_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              supplier.company,
                              supplier.company_name,
                              'OTIF',
                              supplier.otif_count
                            )
                          }
                          className="text-sm text-green-600 hover:underline cursor-pointer"
                          title="View OTIF details"
                        >
                          {supplier.otif_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              supplier.company,
                              supplier.company_name,
                              'Arrears',
                              supplier.arrears_count
                            )
                          }
                          className="text-sm text-red-600 hover:underline cursor-pointer"
                          title="View Arrears details"
                        >
                          {supplier.arrears_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <span
                          className={`text-sm font-semibold ${getOTDColor(supplier.otif_score_percentage || 0)}`}
                        >
                          {supplier.otif_score_percentage?.toFixed(1) || 0}%
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              supplier.company,
                              supplier.company_name,
                              'Qty Diff',
                              supplier.qty_diff
                            )
                          }
                          className="text-sm text-primary hover:underline cursor-pointer"
                          title="View Qty Diff details"
                        >
                          {supplier.qty_diff || 0}
                        </button>
                      </td>

                      {/* Delivery Metrics (GRN) */}
                      <td className="p-3 text-center border-l">
                        <button
                          onClick={() =>
                            onOpenGRNModal(
                              supplier.company,
                              supplier.company_name,
                              'No. of GRNs',
                              supplier.grn_count
                            )
                          }
                          className="text-sm text-primary hover:underline cursor-pointer"
                          title="View GRN details"
                        >
                          {supplier.grn_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              supplier.company,
                              supplier.company_name,
                              'Ontime Deliveries',
                              supplier.ontime_count,
                              { status: 'received', on_hold: false }
                            )
                          }
                          className="text-sm text-green-600 hover:underline cursor-pointer"
                          title="View Ontime deliveries"
                        >
                          {supplier.ontime_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              supplier.company,
                              supplier.company_name,
                              'Late Deliveries',
                              supplier.late_count,
                              { status: 'received', on_hold: true }
                            )
                          }
                          className="text-sm text-red-600 hover:underline cursor-pointer"
                          title="View Late deliveries"
                        >
                          {supplier.late_count || 0}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() =>
                            onOpenPOLineModal(
                              supplier.company,
                              supplier.company_name,
                              'Early Deliveries',
                              supplier.early_count,
                              { status: 'received' }
                            )
                          }
                          className="text-sm text-blue-600 hover:underline cursor-pointer"
                          title="View Early deliveries"
                        >
                          {supplier.early_count || 0}
                        </button>
                      </td>

                      {/* CO2 Emissions */}
                      <td className="p-3 text-center border-l">
                        <span className="text-sm">{supplier.carbon_emission || 0}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Bottom Pagination */}
            {pagination.hasMultiplePages && (
              <div className="px-6 pb-4">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={pagination.goToPage}
                  itemLabel="suppliers"
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
